// Full improved SEO-enhanced React HomePage code
// (Your original structure kept, SEO expanded: canonical, OG, Twitter)

import React, { useRef, useEffect } from "react";
import { ArrowRight } from "lucide-react";
import SubsidiaryCard from "../components/SubsidiaryCard";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import rivertonImg from "../assets/images/riverton.webp";
import homeHero from "../assets/images/home-hero.jpg";
import cleaningImg from "../assets/images/cleaning.jpg";
import securityImg from "../assets/images/security.jpg";
import ictImg from "../assets/images/ict.jpg";
import hrImg from "../assets/images/hr.jpg";
import exploitsImg from "../assets/images/exploits.jpg";
import debtImg from "../assets/images/debt.jpg";
import aboutSection from "../assets/images/about-section.jpg";

// =============================
// Improved SEO Hook
// =============================
const useSEO = ({ title, description, keywords, structuredData, url, image }) => {
  useEffect(() => {
    document.title = title;

    const setMeta = (name, content) => {
      let tag = document.querySelector(`meta[name="${name}"]`);
      if (!tag) {
        tag = document.createElement("meta");
        tag.name = name;
        document.head.appendChild(tag);
      }
      tag.content = content;
    };

    const setPropMeta = (property, content) => {
      let tag = document.querySelector(`meta[property="${property}"]`);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("property", property);
        document.head.appendChild(tag);
      }
      tag.content = content;
    };

    // Basic meta
    setMeta("description", description);
    setMeta("keywords", keywords);

    // Canonical
    let canonical = document.querySelector("link[rel='canonical']");
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = url;

    // Open Graph
    setPropMeta("og:title", title);
    setPropMeta("og:description", description);
    setPropMeta("og:image", image);
    setPropMeta("og:url", url);
    setPropMeta("og:type", "website");

    // Twitter
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", title);
    setMeta("twitter:description", description);
    setMeta("twitter:image", image);

    // JSON-LD
    const existingScript = document.getElementById("structured-data");
    if (existingScript) existingScript.remove();

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "structured-data";
    script.text = JSON.stringify(structuredData);
    document.head.appendChild(script);

    return () => {
      if (script.parentNode) script.parentNode.removeChild(script);
    };
  }, [title, description, keywords, structuredData, url, image]);
};

// =============================
// Main HomePage Component
// =============================
export default function HomePage({ navigateTo }) {
  const heroRef = useRef(null);
  const statsRef = useRef(null);

  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.5], [1, 1.1]);

  // =============================
  // SEO CONFIG
  // =============================
  const seoConfig = {
    title: "Madison Holdings - Leading Business Solutions Provider in Malawi",
    description:
      "Professional cleaning, security, ICT, HR, education and FMCG services across Malawi. Trusted business solutions since 2007.",
    keywords:
      "business solutions Malawi, cleaning Malawi, security Malawi, ICT Malawi, HR Malawi, Exploits University, Riverton Dairy, Aspen Cleaning",
    url: typeof window !== "undefined" ? window.location.href : "https://madisonholdings.mw",
    image: homeHero,
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "Madison Holdings Limited",
      url: "https://madisonholdings.mw",
      logo: "https://madisonholdings.mw/logo.png",
      description:
        "Leading business solutions provider offering cleaning, security, ICT, HR, FMCG and education across Malawi.",
      address: {
        "@type": "PostalAddress",
        addressCountry: "Malawi",
        addressLocality: "Lilongwe",
      },
      contactPoint: {
        "@type": "ContactPoint",
        contactType: "customer service",
        email: "info@madisonholdings.mw",
      },
    },
  };

  useSEO(seoConfig);

  // =============================
  // Data
  // =============================
  const subsidiaries = [
    {
      title: "Aspen Cleaning",
      tag: "Cleaning Services",
      description: "Professional residential and commercial cleaning in Malawi.",
      image: cleaningImg,
      url: "https://aspenmw.com",
      stats: { clients: "250+", satisfaction: "98%" },
    },
    {
      title: "Aspen Security",
      tag: "Security Services",
      description: "Trusted security guards and 24/7 protection across Malawi.",
      image: securityImg,
      url: "https://aspenmw.com",
      stats: { officers: "150+", coverage: "24/7" },
    },
    {
      title: "Aspen ICT",
      tag: "Technology Solutions",
      description: "IT support, networking and software development.",
      image: ictImg,
      url: "https://aspenmw.com",
      stats: { projects: "100+", uptime: "99.9%" },
    },
    {
      title: "Madison Recruitment & Payroll",
      tag: "Human Resources",
      description: "HR, payroll and recruitment services across Malawi.",
      image: hrImg,
      url: "https://jobs.madisonmw.com/",
      stats: { placements: "500+", payroll: "1000+" },
    },
    {
      title: "Exploits University",
      tag: "Education",
      description: "Accredited university offering diverse programs.",
      image: exploitsImg,
      url: "https://exploitsmw.com/",
      stats: { students: "2000+", programs: "15+" },
    },
    {
      title: "Riverton Dairy",
      tag: "FMCG & Dairy",
      description: "Quality yoghurt and dairy products.",
      image: rivertonImg,
      url: "https://rivertonmw.com",
      stats: { products: "20+", outlets: "50+" },
    },
    {
      title: "Debt Collection Services",
      tag: "Financial Services",
      description: "Effective business debt recovery solutions.",
      image: debtImg,
      url: "https://jobs.madisonmw.com/",
      stats: { recovery: "85%", clients: "100+" },
    },
  ];

  const stats = [
    { number: "4", label: "Subsidiaries", suffix: "+" },
    { number: "15", label: "Years Experience", suffix: "+" },
    { number: "99", label: "Client Satisfaction", suffix: "%" },
    { number: "99", label: "Outsourced Business", suffix: "%" },
    { number: "99", label: "Recruitment Options", suffix: "%" },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  const statVariants = {
    hidden: { scale: 0, opacity: 0 },
    visible: (i) => ({
      scale: 1,
      opacity: 1,
      transition: { delay: i * 0.1, type: "spring", stiffness: 100 },
    }),
  };

  // =============================
  // JSX
  // =============================
  return (
    <>
      {/* HERO */}
      <motion.section ref={heroRef} className="hero" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }}>
        <div className="hero-bg-container">
          <motion.div className="hero-bg" style={{ scale, backgroundImage: `url(${homeHero})` }} />
        </div>

        <motion.div className="hero-content" style={{ opacity }}>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            MADISON GROUP
          </motion.h1>

          <motion.p className="hero-subtitle" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            Leading provider of Cleaning, Security, ICT and HR solutions in Malawi.
          </motion.p>

          <motion.button className="hero-cta" onClick={() => navigateTo("contact")}>
            Partner With Us
          </motion.button>
        </motion.div>
      </motion.section>

      {/* STATS */}
      <motion.section ref={statsRef} className="stats-section" variants={containerVariants} initial="hidden" whileInView="visible">
        <div className="stats-container">
          {stats.map((stat, index) => (
            <motion.div key={index} className="stat-item" variants={statVariants} custom={index}>
              <div className="stat-number">
                {stat.number}
                <span>{stat.suffix}</span>
              </div>
              <div className="stat-label">{stat.label}</div>
            </motion.div>
          ))}
        </div>
      </motion.section>

      {/* ABOUT */}
      <motion.section className="about-section" variants={containerVariants} initial="hidden" whileInView="visible">
        <div className="about-content">
          <motion.div className="about-text" variants={itemVariants}>
            <h2>About Madison Holdings</h2>
            <p>
              Madison Holdings provides top-tier cleaning, security, HR, ICT and FMCG solutions across Malawi.
            </p>
            <p>
              Since 2007, we have built a legacy of trust, professionalism and innovation.
            </p>
          </motion.div>

          <motion.div className="about-image" variants={itemVariants}>
            <img src={aboutSection} alt="Madison Holdings office discussion" loading="lazy" />
          </motion.div>
        </div>
      </motion.section>

      {/* SUBSIDIARIES */}
      <motion.section className="subsidiaries-section" variants={containerVariants} initial="hidden" whileInView="visible">
        <motion.div className="section-header" variants={itemVariants}>
          <h2>Our Subsidiaries</h2>
          <p>Delivering business solutions across multiple industries in Malawi.</p>
        </motion.div>

        <motion.div className="subsidiaries-grid" variants={containerVariants}>
          <AnimatePresence>
            {subsidiaries.map((sub, index) => (
              <SubsidiaryCard key={index} {...sub} />
            ))}
          </AnimatePresence>
        </motion.div>
      </motion.section>

      {/* CTA */}
      <motion.section className="cta-section" initial={{ opacity: 0, y: 50 }} whileInView={{ opacity: 1, y: 0 }}>
        <div className="cta-content">
          <motion.h2>Ready to Transform Your Business?</motion.h2>
          <motion.p>Contact Madison Holdings for proven business solutions that deliver results.</motion.p>

          <div className="cta-buttons">
            <motion.button className="btn-primary" onClick={() => navigateTo("contact")}>Get Started</motion.button>
            <motion.button className="btn-secondary" onClick={() => navigateTo("services")}>Explore Services</motion.button>
          </div>
        </div>
      </motion.section>
    </>
  );
}
